import { cloneDeep } from 'lodash';
import { useReducer } from 'react';
import { match } from 'ts-pattern';
import { JsonLocalStorageService } from '../../../api/local-storage-service';
import { TimeRange } from '../../../api/timeranges/types';
import { Domains } from '../../../constants';
import { reducerWithPersistentWrapper } from '../../../context/hooks';
import {
  CalendarYearMonthlyTimesliderGranularity,
  CalendarYearYearlyTimesliderGranularity,
  FinancialYearQuarterlyTimesliderGranularity,
  FinancialYearYearlyTimesliderGranularity,
} from './constants';
import {
  Granularity,
  TimePointSelectorAction,
  TimePointSelectorState,
  TimePointSelectorUpdateAction,
  TimeSliderGranularity,
} from './types';
import { jsonParserReviver } from './utils';

export const timePointSelectorGranularities: TimeSliderGranularity[] = [
  CalendarYearMonthlyTimesliderGranularity,
  CalendarYearYearlyTimesliderGranularity,
  FinancialYearYearlyTimesliderGranularity,
  FinancialYearQuarterlyTimesliderGranularity,
];

export const getInitialTimePointSelectorState = (
  availableTimeRanges: Record<Granularity, TimeRange>
): TimePointSelectorState => {
  const selectedGranularity = CalendarYearMonthlyTimesliderGranularity;
  const range = availableTimeRanges[selectedGranularity.value];
  const date = range.max;
  const initialState = {
    date,
    range,
    granularities: timePointSelectorGranularities,
    selectedGranularity,
    availableTimeRanges,
  };
  return {
    ...initialState,
    initialState: cloneDeep(initialState),
  };
};

export const useTimePointSelectorWithPersistence = (
  domain: Domains,
  init: TimePointSelectorState
) => {
  const persistenceKey = `${domain}-timepointselector-v1`;
  const localStorageService =
    new JsonLocalStorageService<TimePointSelectorState>(
      persistenceKey,
      jsonParserReviver
    );
  const storedItems: TimePointSelectorState | null = localStorageService.get();
  const state = storedItems ?? { ...init };
  return useReducer(
    reducerWithPersistentWrapper(reducer, localStorageService),
    state
  );
};

export const useTimePointSelector = (init: TimePointSelectorState) => {
  return useReducer(reducer, init);
};

export const reducer = (
  state: TimePointSelectorState,
  action: TimePointSelectorAction
): TimePointSelectorState => {
  return match(action)
    .with({ type: 'reset' }, () => {
      const newState = state.initialState;
      return { ...newState, initialState: newState };
    })
    .with({ type: 'update-point' }, (a: TimePointSelectorUpdateAction) => {
      return {
        ...state,
        date: a.date,
      };
    })
    .exhaustive();
};
